import React, { useEffect, useState } from "react";
import api from "../../../../api/client";

// What a "no" on a Yes / No task leads to. "fix" asks why as well — the
// reason is what the fixer is sent.
const ON_NO_OPTIONS = [
  { value: "record", name: "Just record it", desc: "The no shows on the task card. Nothing else happens." },
  { value: "ask_why", name: "Ask why", desc: "They're asked for a short reason straight after." },
  { value: "fix", name: "Ask why and raise a fix", desc: "The reason goes to someone to fix. The next check-in shows whether it worked." },
];

const WRITTEN_TYPES = [
  { value: "text", name: "Text", hint: "They reply in their own words — typed, or as a voice note if voice is switched on." },
  { value: "number", name: "Number", hint: "They reply with a number, like a reading or a count. Anything that isn't a number is sent back." },
  { value: "photo", name: "Photo", hint: "They reply with a photo, or upload one from My Tasks." },
];

const FIX_WITHIN_OPTIONS = [1, 2, 3, 7, 14];

const WEEKDAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

function onNoOf(task) {
  if (task?.raiseFixOnNo) return "fix";
  if (task?.askReasonOnNo) return "ask_why";
  return "record";
}

function daysLabel(d) {
  if (d === 7) return "1 week";
  if (d === 14) return "2 weeks";
  return `${d} ${d === 1 ? "day" : "days"}`;
}

function formatTime(hhmm) {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const suffix = h >= 12 ? "pm" : "am";
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${suffix}`;
}

// One collapsible section. Closed, it shows a one-line summary of what is set
// inside, so the whole task reads at a glance without opening anything.
function Tile({ id, title, summary, open, onToggle, invalid, onNext, children }) {
  return (
    <section className={`at-tile${open ? " open" : ""}${invalid ? " invalid" : ""}`}>
      <button
        type="button"
        className="at-tile-head"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        aria-controls={`at-tile-${id}`}
      >
        <span className="at-tile-text">
          <span className="at-tile-title">{title}</span>
          {!open && <span className="at-tile-summary">{summary}</span>}
        </span>
        <svg className="at-tile-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="at-tile-body" id={`at-tile-${id}`}>
          {children}
          {onNext && (
            <div className="at-tile-foot">
              <button type="button" className="at-btn-next" onClick={onNext}>Next</button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function AllottedTaskModal({ task, onClose, onSaved }) {
  const editing = Boolean(task);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errorTile, setErrorTile] = useState(null);
  // A new task opens on its first step; an existing one opens collapsed so
  // the summaries show what is set.
  const [openTiles, setOpenTiles] = useState(() => new Set(editing ? [] : ["task"]));

  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [mode, setMode] = useState(task?.mode || "recurring");
  const [dueBy, setDueBy] = useState(task?.dueBy ? task.dueBy.slice(0, 10) : "");

  // "How they answer" is two choices — Yes / No or written, then what kind of
  // written reply — that map onto the one responseType the API stores.
  const [answerKind, setAnswerKind] = useState(task && task.responseType !== "yes_no" ? "written" : "yes_no");
  const [writtenType, setWrittenType] = useState(
    task && task.responseType !== "yes_no" ? task.responseType : "text",
  );
  const [onNo, setOnNo] = useState(onNoOf(task));
  const [fixerId, setFixerId] = useState(task?.fixerId || "");
  const [fixWithinDays, setFixWithinDays] = useState(task?.fixWithinDays ?? 2);
  const [allowPhoto, setAllowPhoto] = useState(task?.allowPhoto || false);

  const [frequency, setFrequency] = useState(task?.frequency || "daily");
  const [timeOfDay, setTimeOfDay] = useState(task?.timeOfDay || "09:00");
  const [dayOfWeek, setDayOfWeek] = useState(task?.dayOfWeek ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(task?.dayOfMonth ?? 1);
  const [assigneeIds, setAssigneeIds] = useState(task?.assignees?.map((a) => a.id) || []);

  const responseType = answerKind === "yes_no" ? "yes_no" : writtenType;

  useEffect(() => {
    api
      .get("/manufacturing/allotted-tasks/assignable-users")
      .then((res) => setUsers(res.data.data || []))
      .catch(() => setError("Couldn't load the people list. Refresh and try again."))
      .finally(() => setLoadingUsers(false));
  }, []);

  const toggleTile = (id) => {
    setOpenTiles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearError = (tile) => {
    if (errorTile === tile) {
      setError("");
      setErrorTile(null);
    }
  };

  // Opens the tile the problem is in, so the error is never hidden in a
  // collapsed section.
  const showError = (tile, message) => {
    setError(message);
    setErrorTile(tile);
    setOpenTiles((prev) => new Set(prev).add(tile));
  };

  const toggleAssignee = (id) => {
    setAssigneeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    clearError("people");
  };

  const chooseMode = (next) => {
    setMode(next);
    // Reminders need a clear "done" to stop on.
    if (next === "until_done") setAnswerKind("yes_no");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return showError("task", "Give the task a title.");
    if (assigneeIds.length === 0) return showError("people", "Pick at least one person to check in with.");

    setSaving(true);
    setError("");
    setErrorTile(null);
    const isYesNo = responseType === "yes_no";
    const body = {
      title: title.trim(),
      description: description.trim(),
      responseType,
      mode,
      askReasonOnNo: isYesNo && onNo !== "record",
      raiseFixOnNo: isYesNo && onNo === "fix",
      fixerId: fixerId || null,
      fixWithinDays: Number(fixWithinDays),
      allowPhoto: responseType !== "photo" && allowPhoto,
      dueBy: mode === "until_done" && dueBy ? new Date(`${dueBy}T23:59:59`).toISOString() : null,
      frequency,
      timeOfDay,
      dayOfWeek: frequency === "weekly" ? Number(dayOfWeek) : undefined,
      dayOfMonth: frequency === "monthly" ? Number(dayOfMonth) : undefined,
      assigneeIds,
    };

    try {
      if (editing) {
        await api.patch(`/manufacturing/allotted-tasks/${task.id}`, body);
      } else {
        await api.post("/manufacturing/allotted-tasks", body);
      }
      onSaved(editing ? "Task updated" : "Task created");
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Couldn't save the task.");
      setSaving(false);
    }
  };

  const noChannelSelected = users.filter(
    (u) => assigneeIds.includes(u.id) && !u.hasTelegram && !u.hasWhatsapp,
  );
  const fixerName = users.find((u) => u.id === fixerId)?.name || "whoever said No";

  // One line per closed tile.
  const summaries = {
    task: title.trim() || "No title yet",
    kind:
      mode === "until_done"
        ? `Remind until done${dueBy ? ` · due ${new Date(dueBy).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}`
        : "Recurring check-in",
    answer: [
      answerKind === "yes_no" ? "Yes / No" : `Written · ${WRITTEN_TYPES.find((t) => t.value === writtenType)?.name}`,
      answerKind === "yes_no" && onNo === "ask_why" && "asks why on No",
      answerKind === "yes_no" && onNo === "fix" && `No → fix by ${fixerName}`,
      responseType !== "photo" && allowPhoto && "photo optional",
    ].filter(Boolean).join(" · "),
    schedule:
      frequency === "daily"
        ? `Every day at ${formatTime(timeOfDay)}`
        : frequency === "weekly"
          ? `Every ${WEEKDAYS.find((d) => d.value === Number(dayOfWeek))?.label} at ${formatTime(timeOfDay)}`
          : `Day ${dayOfMonth} of each month at ${formatTime(timeOfDay)}`,
    people:
      assigneeIds.length === 0
        ? "Nobody picked yet"
        : users.length > 0
          ? users.filter((u) => assigneeIds.includes(u.id)).map((u) => u.name).join(", ")
          : `${assigneeIds.length} ${assigneeIds.length === 1 ? "person" : "people"}`,
  };

  // Next closes this tile and opens the one after it. The last tile has
  // none — the Create button below is its next step.
  const TILE_ORDER = ["task", "kind", "answer", "schedule", "people"];

  const goNext = (id) => {
    if (id === "task" && !title.trim()) return showError("task", "Give the task a title.");
    clearError(id);
    const following = TILE_ORDER[TILE_ORDER.indexOf(id) + 1];
    setOpenTiles((prev) => {
      const next = new Set(prev);
      next.delete(id);
      if (following) next.add(following);
      return next;
    });
  };

  const tile = (id) => ({
    id,
    open: openTiles.has(id),
    onToggle: toggleTile,
    summary: summaries[id],
    invalid: errorTile === id,
    onNext: id === TILE_ORDER[TILE_ORDER.length - 1] ? null : () => goNext(id),
  });

  return (
    <div className="at-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="at-modal" role="dialog" aria-modal="true" aria-label={editing ? "Edit task" : "New task"}>
        <div className="at-modal-head">
          <h3>{editing ? "Edit task" : "New allotted task"}</h3>
          <button type="button" className="at-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="at-error">{error}</div>}

          <div className="at-tiles">
            <Tile title="Task" {...tile("task")}>
              <div className="at-field">
                <label className="at-label" htmlFor="at-title">Task</label>
                <input
                  id="at-title"
                  className="at-input"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); clearError("task"); }}
                  placeholder="Clean and log the CNC coolant levels"
                  autoFocus={!editing}
                />
              </div>
              <div className="at-field" style={{ marginBottom: 0 }}>
                <label className="at-label" htmlFor="at-desc">
                  Details <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>(optional)</span>
                </label>
                <textarea
                  id="at-desc"
                  className="at-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Anything the assignee needs to know before answering."
                />
              </div>
            </Tile>

            <Tile title="What kind of task" {...tile("kind")}>
              <div className="at-mode-picker">
                <button
                  type="button"
                  className={`at-mode${mode === "recurring" ? " on" : ""}`}
                  onClick={() => chooseMode("recurring")}
                  aria-pressed={mode === "recurring"}
                >
                  <span className="at-mode-name">Recurring check-in</span>
                  <span className="at-mode-desc">Asks again every period. Each answer stands on its own and the task never ends.</span>
                </button>
                <button
                  type="button"
                  className={`at-mode${mode === "until_done" ? " on" : ""}`}
                  onClick={() => chooseMode("until_done")}
                  aria-pressed={mode === "until_done"}
                >
                  <span className="at-mode-name">Remind until done</span>
                  <span className="at-mode-desc">Keeps reminding the same person until they mark it done, then stops for them.</span>
                </button>
              </div>

              {mode === "until_done" && (
                <div className="at-field" style={{ marginTop: 14, marginBottom: 0 }}>
                  <label className="at-label" htmlFor="at-dueby">
                    Due by <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>(optional)</span>
                  </label>
                  <input id="at-dueby" type="date" className="at-input" value={dueBy} onChange={(e) => setDueBy(e.target.value)} />
                  <p className="at-hint">
                    Reminders carry on either way — past this date the task is tagged overdue so it stands out here.
                  </p>
                </div>
              )}
            </Tile>

            <Tile title="How they answer" {...tile("answer")}>
              <div className="at-segmented">
                <button
                  type="button"
                  className={`at-seg${answerKind === "yes_no" ? " on" : ""}`}
                  onClick={() => setAnswerKind("yes_no")}
                  aria-pressed={answerKind === "yes_no"}
                >
                  Yes / No
                </button>
                <button
                  type="button"
                  className={`at-seg${answerKind === "written" ? " on" : ""}`}
                  onClick={() => setAnswerKind("written")}
                  aria-pressed={answerKind === "written"}
                  disabled={mode === "until_done"}
                  title={mode === "until_done" ? "Remind-until-done tasks have to be Yes / No" : undefined}
                >
                  Written reply
                </button>
              </div>
              <p className="at-hint">
                {mode === "until_done"
                  ? "Yes / No only — the reminders need a clear signal to know when to stop."
                  : answerKind === "yes_no"
                    ? "Shows two buttons — one tap and the answer is logged."
                    : WRITTEN_TYPES.find((t) => t.value === writtenType)?.hint}
              </p>

              {answerKind === "written" && (
                <div className="at-subfield">
                  <span className="at-sublabel">Written as</span>
                  <div className="at-segmented">
                    {WRITTEN_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        className={`at-seg${writtenType === t.value ? " on" : ""}`}
                        onClick={() => setWrittenType(t.value)}
                        aria-pressed={writtenType === t.value}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {answerKind === "yes_no" && (
                <div className="at-subfield">
                  <span className="at-sublabel">If the answer is No</span>
                  <div className="at-mode-picker at-onno-picker">
                    {ON_NO_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        className={`at-mode${onNo === o.value ? " on" : ""}`}
                        onClick={() => setOnNo(o.value)}
                        aria-pressed={onNo === o.value}
                      >
                        <span className="at-mode-name">{o.name}</span>
                        <span className="at-mode-desc">{o.desc}</span>
                      </button>
                    ))}
                  </div>

                  {onNo === "fix" && (
                    <>
                      <div className="at-schedule-row" style={{ marginTop: 12 }}>
                        <label className="at-fix-field">
                          <span className="at-fix-label">Who fixes it</span>
                          <select className="at-select" value={fixerId} onChange={(e) => setFixerId(e.target.value)}>
                            <option value="">The person who said No</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </label>
                        <label className="at-fix-field">
                          <span className="at-fix-label">Fix within</span>
                          <select className="at-select" value={fixWithinDays} onChange={(e) => setFixWithinDays(e.target.value)}>
                            {FIX_WITHIN_OPTIONS.map((d) => <option key={d} value={d}>{daysLabel(d)}</option>)}
                          </select>
                        </label>
                      </div>
                      <p className="at-hint">
                        The fixer gets it on Telegram or WhatsApp and in My Tasks, and is reminded daily until they tap
                        Fixed. A Yes on the next check-in closes it; another No sends it back.
                      </p>
                    </>
                  )}
                </div>
              )}

              {responseType !== "photo" && (
                <div className="at-subfield">
                  <span className="at-sublabel">Photos</span>
                  <label className="at-check" style={{ marginTop: 0 }}>
                    <input type="checkbox" checked={allowPhoto} onChange={(e) => setAllowPhoto(e.target.checked)} />
                    Let them add a photo with their answer
                  </label>
                  <p className="at-hint">
                    Optional for them — after answering they're offered the chance to send one, and it shows next to
                    their answer.
                  </p>
                </div>
              )}
            </Tile>

            <Tile title={mode === "until_done" ? "How often to remind" : "How often to ask"} {...tile("schedule")}>
              <div className="at-schedule-row">
                <select className="at-select" value={frequency} onChange={(e) => setFrequency(e.target.value)} aria-label="Frequency">
                  <option value="daily">Every day</option>
                  <option value="weekly">Every week</option>
                  <option value="monthly">Every month</option>
                </select>

                {frequency === "weekly" && (
                  <select className="at-select" value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)} aria-label="Day of the week">
                    {WEEKDAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                )}

                {frequency === "monthly" && (
                  <select className="at-select" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} aria-label="Day of the month">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Day {d}</option>)}
                  </select>
                )}

                <input
                  type="time"
                  className="at-input"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                  aria-label="Time of day"
                />
              </div>
              <p className="at-hint">Times are IST. Months shorter than the chosen day use their last day instead.</p>
            </Tile>

            <Tile
              title={`${mode === "until_done" ? "Who to remind" : "Who checks in"} (${assigneeIds.length} selected)`}
              {...tile("people")}
            >
              {loadingUsers ? (
                <p className="at-hint">Loading people…</p>
              ) : (
                <div className="at-people">
                  {users.map((u) => (
                    <label key={u.id} className="at-person">
                      <input type="checkbox" checked={assigneeIds.includes(u.id)} onChange={() => toggleAssignee(u.id)} />
                      {u.name}
                      {!u.hasTelegram && !u.hasWhatsapp && <span className="at-person-flag">No messaging channel</span>}
                    </label>
                  ))}
                </div>
              )}
              {noChannelSelected.length > 0 && (
                <p className="at-hint" style={{ color: "#b45309" }}>
                  {noChannelSelected.map((u) => u.name).join(", ")} {noChannelSelected.length === 1 ? "has" : "have"} no
                  Telegram or WhatsApp linked — they'll see the check-in in My Tasks but get no message.
                </p>
              )}
            </Tile>
          </div>

          <div className="at-modal-foot">
            <button type="button" className="at-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="at-btn-primary" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
